<template>
    <!-- Council 004 Surface 4: the 100% voice-cap modal on Start Voice Chat. A
         dedicated modal (top-up offer + text-chat fallback), NOT the generic
         tier-limit modal. Closable (X / Esc / backdrop) — the secondary action keeps
         the user practising rather than trapping them. -->
    <!-- A plan with no monthly minutes (Reader) never ran "out of" them: say so. -->
    <Modal :title="base > 0 ? t('subscription.voice-modal.title') : t('subscription.voice-modal.title-none')" size="md" :modelValue="open" @close="closeVoiceCapModal">
        <!-- No trigger: an empty slot would render pilotui's default unlabeled button. -->
        <template #trigger><span class="hidden" /></template>
        <div class="space-y-2 p-2 text-center">
            <p v-if="base > 0" class="text-gray-700 dark:text-gray-300">{{ t('subscription.voice-modal.body1', { total: base }) }}</p>
            <p v-if="renewsOn && base > 0" class="text-sm text-gray-500">{{ t('subscription.voice-meter.resets', { date: renewsOn }) }}</p>
            <p class="text-gray-700 dark:text-gray-300">{{ t('subscription.voice-modal.body2', { price: thirtyPrice }) }}</p>
        </div>
        <template #footer>
            <div class="flex w-full flex-col gap-2">
                <Button
                    color="primary"
                    block
                    :loading="loadingKey === 'topup_30'"
                    :label="t('subscription.voice-modal.add30', { price: thirtyPrice })"
                    @click="onAdd30"
                />
                <Button block outline color="primary" :label="t('subscription.voice-modal.use-text')" @click="onUseText" />
                <button type="button" class="mt-1 text-center text-xs text-primary hover:underline" @click="showPicker = true">
                    {{ t('subscription.voice-modal.see-all') }}
                </button>
            </div>
        </template>
    </Modal>

    <TopUpPickerModal v-model="showPicker" />
</template>

<script setup lang="ts">
    import { ref, computed } from 'vue';
    import { Button } from 'pilotui/elements';
    import { Modal } from 'pilotui/complex';
    import TopUpPickerModal from '~/components/TopUpPickerModal.vue';
    import { useVoiceCapModal } from '~/composables/useVoiceCapModal';
    import { useVoiceBalance } from '~/composables/useVoiceBalance';
    import { useVoiceTopUp, TOP_UP_PACKS, localizeGbp } from '~/composables/useVoiceTopUp';
    import { encodeSessionRequest } from '~/utils/livePractice';

    const { t } = useI18n();
    const router = useRouter();
    const { open, request, closeVoiceCapModal } = useVoiceCapModal();
    const { base, renewalDate } = useVoiceBalance();
    const { loadingKey, buyPack } = useVoiceTopUp();

    const showPicker = ref(false);
    const thirtyPrice = computed(() => localizeGbp(TOP_UP_PACKS[0].gbp));
    const renewsOn = computed(() => {
        const d = renewalDate.value;
        if (!d) return '';
        try {
            return new Date(d).toLocaleDateString(undefined, { day: 'numeric', month: 'long' });
        } catch {
            return '';
        }
    });

    // Primary skips the picker — straight to the 30-min checkout (the user is mid-task).
    function onAdd30() {
        buyPack('topup_30');
        closeVoiceCapModal();
    }
    // Secondary keeps the user practising: the same request, as a text session. With no
    // request to carry, back to the setup form with Text already selected.
    function onUseText() {
        closeVoiceCapModal();
        if (!request.value) {
            router.push('/sessions/new?mode=text');
            return;
        }
        const session = encodeSessionRequest({ ...request.value, mode: 'text' });
        router.push(`/practice/live-session?session=${encodeURIComponent(session)}`);
    }
</script>
